/*
 * @Author: Kuaiyin FE
 * @Date: 2020-12-29 15:57:07
 * @Description: 公共方法
 * @FilePath: /vision-h5/src/utils/common.js
 */

/**
 * @description: 图片预加载 MAP
 * key 为路由 path
 * value 为需要预加载的图片文件名列表
 */

export const prefetchMap = {
	'/task': ['fortune-wheel'],
	'/welfare': ['fortune-wheel'],
};

/**
 * @description: 预加载图片
 * @param { Array } imgFolderList 文件夹名列表
 */

export const preloadImages = (imgFolderList) => {
	const imageList = getImageList(imgFolderList);

	imageList.forEach((item) => {
		let image = new Image();
		image.src = item;
	});
};

/**
 * @description: 获取文件夹下图片列表
 * @param { Array } imgFolders 文件夹名列表
 * @return { Array } imageList 预加载图片url列表
 */

export const getImageList = (imgFolders) => {
	const files = require.context('@/assets/images', true, /.(png|jpg|jpeg|gif|bmp|webp)$/).keys();
	const imageList = [];

	if (Object.prototype.toString.call(imgFolders) === '[object Array]') {
		imgFolders.forEach((folderName) => {
			files.forEach((filePath) => {
				const temp = filePath.match(/\/.*\//g);

				if (temp) {
					const pathList = temp[0].split('/');
					const rightFolder = pathList[pathList.length - 2];

					if (rightFolder === folderName) {
						const imageUrl = require('@/assets/images' + filePath.substring(1));

						if (!/^data:/.test(imageUrl)) {
							// base64 的图片不需要预加载
							imageList.push(imageUrl);
						}
					}
				}
			});
		});
	}

	return imageList;
};

/**
 * @description: 格式化金币/现金 最多保留两位小数 单位：万 || 亿
 * @param {number} n - 数目
 * @return {string} 格式化过的字符串
 */

export const formatCoinNumber = function (n) {
	n = +n;
	if (n < 10000) {
		return n;
	} else if (n < 100000000) {
		return (Math.floor((n / 10000) * 100) / 100).toFixed(2) + '万';
	} else {
		return (Math.floor((n / 100000000) * 100) / 100).toFixed(2) + '亿';
	}
};

/**
 * @description [格式化日期]
 * @param { string }  fmt [日期格式]
 * @param { Date }  date [日期]
 * @return { String }
 */
export function dateFtt(fmt, date) {
	let o = {
		'M+': date.getMonth() + 1, // 月份
		'd+': date.getDate(), // 日
		'h+': date.getHours(), // 小时
		'm+': date.getMinutes(), // 分
		's+': date.getSeconds(), // 秒
		'q+': Math.floor((date.getMonth() + 3) / 3), // 季度
		'S+': date.getMilliseconds(), // 毫秒
	};

	if (/(y+)/.test(fmt)) fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length));
	for (let k in o) {
		if (new RegExp('(' + k + ')').test(fmt)) {
			fmt = fmt.replace(RegExp.$1, RegExp.$1.length == 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length));
		}
	}

	return fmt;
}

/**
 * @description: 初始化下拉加载功能
 * @param { Function } fn 异步加载数据方法,
 * 需要返回一个 Promise 对象 => resolve({ isFinish: Boolean, list: Array })
 * isFinish: 数据是否加载完毕；list： 数据列表
 * @param { Object } context 方法执行环境上下文
 * @param { Array } args 方法参数
 * @param { Number } heightToBottom contentBox 底部距窗口底部的距离（单位 px）
 * @param { Document } contentBox 列表容器 DOM 元素
 * @return { Object } 返回一个实例对象
 */
export function slideUpLoadInit({ fn, context, args = [], heightToBottom = 43, contentBox }) {
	const tipEle = document.createElement('p');
	tipEle.classList.add('loading-tip');
	tipEle.id = 'up-slide-tip';

	let timer = null;
	let isLoading = false;
	let isFinished = false;
	let tip = '加载中...';

	tipEle.innerText = tip;
	contentBox && contentBox.appendChild(tipEle);
	const windowHeight = document.documentElement.clientHeight || document.body.clientHeight;

	// 数据请求方法
	const callFn = async () => {
		isLoading = true;
		const res = await fn.apply(context, args);
		console.log('下拉列表返回值：', res);

		isFinished = res && res.isFinish;
		isLoading = false;

		if (res && res.list && !res.list.length) {
			tip = '暂无数据';
			tipEle.innerText = tip;
		} else if (isFinished) {
			tip = '已经到底啦～';
			tipEle.innerText = tip;
		} else {
			tipEle.style.display = 'none';
			tipEle.remove();
		}
	};

	// 请求数据
	tipEle.style.display = 'block';
	callFn();

	return {
		bindLoading: function () {
			contentBox.addEventListener('scroll', () => {
				if (timer) {
					clearTimeout(timer);
					timer = null;
				}

				const docHeight = contentBox.scrollHeight;
				const scrollTop = contentBox.pageYOffset || contentBox.scrollTop;
				const isToBottom = windowHeight + scrollTop > docHeight - heightToBottom;

				if (!isLoading && !isFinished && isToBottom) {
					tipEle.style.display = 'block';
					contentBox && contentBox.appendChild(tipEle);
					timer = setTimeout(callFn, 100);
				}
			});
		},
		changeFinish: function (status) {
			isFinished = status;
		},
	};
}

/**
 * @description 判断 dom 元素是否在视口中
 * @param { Document } el
 * @param { Number } offset 相对于视口底部的偏移量
 * @param
 * */

export const isInViewPort = function (el, offset = 0) {
	const viewHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
	const top = el && el.getBoundingClientRect && el.getBoundingClientRect() && el.getBoundingClientRect().top;

	return top < viewHeight - offset;
};

// 是否允许页面滚动
export function allowedPageScroll(code, top) {
	if (code) {
		document.body.style.overflow = '';
		// document.body.style.position = '';
		if (top) {
			document.documentElement.scrollTop = top;
		}
	} else {
		document.body.style.overflow = 'hidden';
		// document.body.style.position = 'fixed';
	}
}

// 将时间转为时分
export function convertSeconds(seconds, type) {
	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	const remainingSeconds = seconds % 60;
	if (type == 'numerical') {
		return [hours, remainingSeconds > 0 ? minutes + 1 : minutes];
	} else {
		return `${hours}小时${remainingSeconds > 0 ? minutes + 1 : minutes}分钟`;
	}
}

// 是否是本地开发
export function isLocalDevelopment() {
	const env = process.env.NODE_ENV;
	const port = window.location.port;
	console.log('isLocalDevelopment env port', env, port);
	if (env == 'development' && port && port != 80 && port != 443) {
		return true;
	} else {
		return false;
	}
}



// WEBPACK FOOTER //
// ./src/utils/common.js